import React, { useContext, useCallback, useMemo, useState } from "react";
import { Flex, Box } from "reflexbox";
import { getProducts } from "../../requests/productManagementRequests";
import { useQuery } from "../Hooks";
import { Columns } from "./columns";
import Table from "../UI/Table";
import UserContext from "../../context/user/UserContext";
import ProductCard from "./ProductCard";
import ConfiguratorContext from "../../context/configurator/ConfiguratorContext";
import Card from "../UI/Card";
import _ from "lodash";
import { Body, Helper } from "../UI/Text";
import PageView from "./PageView";
import formatter from "../../modules/formatter";
import { useTheme } from "styled-components";

const ProductCatalog = ({
  inline = false,
  onProductClick,
  isLoading: _isLoading = false,
  pageView = false,
}) => {
  const theme = useTheme();
  const {
    loggedIn,
    pid,
    isLoading: userLoading,
    activeUser,
  } = useContext(UserContext);
  const { quote } = useContext(ConfiguratorContext);

  const forcedFilters = useMemo(() => {
    if (inline) return { limit: 6 };
    return undefined;
  }, [inline]);

  const queryVariables = useMemo(() => {
    if (inline)
      return {
        isQuickConfig: true,
        isNonConfigurable: false,
        pid,
        gid: quote?.groupId,
        erpId: quote?.customer?.erpId,
      };
    return {
      pid,
      erpId: quote?.customer?.erpId,
      gid:
        pageView && activeUser?.roleLevel !== 0
          ? activeUser?.groupId
          : quote?.groupId,
    };
  }, [activeUser, inline, pageView, pid, quote]);

  const attributes = useMemo(
    () =>
      pageView || !inline
        ? [
            "ProductNumber",
            "Name",
            "ID",
            "ApplicationInstance",
            "ApplicationName",
            "isNonConfigurable",
            "isQuickConfig",
            "isNonConfigurable",
            "HeaderId",
            "img",
            "base64Image",
            "ImageUrl",
            "defaultPrice",
            "Description",
            "HeaderId",
            "Overview",
            "quantityInStock",
            "priceCode",
            "Discount",
            "PortalPricingEnabled",
            "RelatedItem",
          ]
        : ["ID", "img", "base64Image", "ImageUrl"],
    [pageView, inline]
  );

  const { data, refetchQuery, isLoading } = useQuery({
    queryID: inline ? "quickConfig" : pageView ? "productPageView" : "products",
    request: getProducts,
    forcedFilters,
    queryVariables,
    attributes,
    skip: !loggedIn || _isLoading || userLoading,
  });

  const [showPreview, setShowPreview] = useState(false);

  const gridData = useMemo(() => {
    
    if (_.isArray(data?.rows)) {
      if (pageView) {
        return data.rows.map((product) => {
          const discPrice = product?.defaultPrice * ((product?.discountPercentage * -1) / 100);
          const image =
            product?.img || product?.base64Image || product?.ImageUrl;

          
          return {
            ...product,
            image,
            title: product?.Name,

            iconName: !!image ? undefined : "faBox",
            elements: 
            activeUser?.roleLevel !== 0 ? 
            [
              <Helper lines={2} ellipsis my="1rem" center>
                {product?.Description}
              </Helper>,
              <Body linethrough right>
                ${formatter.currency(product?.defaultPrice)}
              </Body>,
              <Body align color={theme.secondary} right>                
                ${formatter.currency(product?.defaultPrice-discPrice)}
              </Body>,
            ] : [
              <Helper lines={2} ellipsis my="1rem" center>
              {product?.Description}
              </Helper>,
              <Body right>
                ${formatter.currency(product?.defaultPrice)}
              </Body>,
              ]              
          };
        });
      }
      return data.rows.map((product) => (
        <ProductCard
          key={`${product.ID}|catalog`}
          inline={inline}
          onProductClick={onProductClick}
          product={product}
        />
      ));
    }

    return null;
  }, [data, inline, onProductClick, pageView]);

  if (inline) return gridData;

  if (pageView)
    return (
      <PageView
        data={data}
        gridData={gridData}
        //refetchQuery={refetchQuery}
        setShowPreview={setShowPreview}
        showPreview={showPreview}
        isLoading={isLoading}
      />
    );

  return (
    <Flex
      width={
        inline
          ? "auto"
          : ["100vw", "100vw", "100vw", "100vw", "100vw", "100vw", "1136px"]
      }
      height="80vh"
      p={0}
      style={{ zIndex: "9", position: "relative" }}
    >
      <Card width={1} overflowY="scroll">
        <Box mt="-1rem" mb="-2rem" width={1}>
          <Table
            hideCreate
            backButtonIcon="faXmark"
            backButtonColor={theme.danger}
            createButtonText={null}
            isLoading={isLoading}
            title="Product Catalog"
            columns={Columns(onProductClick)}
            gridSize="medium"
            gridData={gridData}
            data={data?.rows || []}
            customGrid
            totalCount={data?.count}
            hideSearch={inline}
            hideProperties={inline}
            onCardClick={(row) => onProductClick(row.id)}
          />
        </Box>
      </Card>
    </Flex>
  );
};

export default ProductCatalog;
